import type { ChangeEvent } from 'react';
import { GEWERK } from '../koordination/gemeinsam';

/**
 * Der serverseitige Filter der KI-Ansicht (Dokument 36, Abschnitt B) —
 * dieselben Parameter wie `GET .../ai-completion/findings`
 * (`CompletionEndpoints.CompletionFindingsFilter`). Anders als in
 * Koordinations `Filterleiste.tsx` sind hier eigene Felder nötig: Die
 * KI-Ansicht kennt weder Lösungsart noch Korb, dafür Befundart, Planstatus
 * und Nennweite — Werte, die es in der Koordination nicht gibt.
 */
export interface AiCompletionFilter {
  befundart: string;
  planstatus: string;
  gewerk: string;
  medium: string;
  nennweiteVon: string;
  nennweiteBis: string;
  suche: string;
}

export const AI_COMPLETION_FILTER_LEER: AiCompletionFilter = {
  befundart: 'alle',
  planstatus: '',
  gewerk: '',
  medium: '',
  nennweiteVon: '',
  nennweiteBis: '',
  suche: '',
};

/** Die fünf Plan-Kennzahlen aus `CompletionEndpoints.PlanStatusEimer`. */
const PLANSTATUS = [
  ['offen', 'offen'],
  ['vorgeschlagen', 'vorgeschlagen'],
  ['freigegeben', 'freigegeben'],
  ['ausgefuehrt', 'ausgeführt'],
  ['gescheitert', 'gescheitert'],
] as const;

/** Dieselben zwei Befundarten wie der Baum (Dokument 36, Abschnitt B) plus 'alle'. */
const BEFUNDARTEN = [
  ['alle', 'alle'],
  ['anschluss', 'offener Anschluss'],
  ['kollision', 'Kollision'],
] as const;

function zahl(e: ChangeEvent<HTMLInputElement>): string {
  return e.target.value;
}

/**
 * Die Filterleiste der KI-Ansicht — eine Zeile, umbrechend, alle Werte
 * kontrolliert von außen (URL-Zustand liegt in AiCompletion.tsx). Rein
 * anzeigend, ruft selbst kein `api.*` auf — dasselbe Muster wie
 * `koordination/Filterleiste.tsx`.
 */
export function Filterleiste({
  filter, onChange, gezeigt, gesamt,
}: {
  filter: AiCompletionFilter;
  onChange: (f: AiCompletionFilter) => void;
  gezeigt: number;
  gesamt: number;
}) {
  const setzen = (teil: Partial<AiCompletionFilter>) => onChange({ ...filter, ...teil });

  return (
    <div className="karte" style={{
      display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
      padding: 10,
    }}>
      <label style={{ fontSize: 11 }}>
        Befundart<br />
        <select value={filter.befundart}
          onChange={(e) => setzen({ befundart: e.target.value })}>
          {BEFUNDARTEN.map(([wert, text]) => (
            <option key={wert} value={wert}>{text}</option>
          ))}
        </select>
      </label>

      <label style={{ fontSize: 11 }}>
        Planstatus<br />
        <select value={filter.planstatus}
          onChange={(e) => setzen({ planstatus: e.target.value })}>
          <option value="">alle</option>
          {PLANSTATUS.map(([wert, text]) => (
            <option key={wert} value={wert}>{text}</option>
          ))}
        </select>
      </label>

      <label style={{ fontSize: 11 }}>
        Gewerk<br />
        <select value={filter.gewerk}
          onChange={(e) => setzen({ gewerk: e.target.value })}>
          <option value="">alle</option>
          {Object.entries(GEWERK).map(([k, name]) => (
            <option key={k} value={k}>{name}</option>
          ))}
        </select>
      </label>

      <label style={{ fontSize: 11 }}>
        Medium (System)<br />
        <input type="text" style={{ width: 140 }}
          placeholder="z. B. Heizung"
          value={filter.medium}
          onChange={(e) => setzen({ medium: e.target.value })} />
      </label>

      <label style={{ fontSize: 11 }}>
        Nennweite von (mm)<br />
        <input type="number" min={0} style={{ width: 90 }}
          value={filter.nennweiteVon}
          onChange={(e) => setzen({ nennweiteVon: zahl(e) })} />
      </label>

      <label style={{ fontSize: 11 }}>
        Nennweite bis (mm)<br />
        <input type="number" min={0} style={{ width: 90 }}
          value={filter.nennweiteBis}
          onChange={(e) => setzen({ nennweiteBis: zahl(e) })} />
      </label>

      <label style={{ fontSize: 11 }}>
        Suche (Element, System, Befund-Id)<br />
        <input type="text" style={{ width: 220 }}
          value={filter.suche}
          onChange={(e) => setzen({ suche: e.target.value })} />
      </label>

      <div className="schwach" style={{ fontSize: 12, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
        {gezeigt} von {gesamt}
      </div>

      <button type="button" className="knopf"
        onClick={() => onChange(AI_COMPLETION_FILTER_LEER)}>
        Filter zurücksetzen
      </button>
    </div>
  );
}
